import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { LOCALES, type Locale, DEFAULT_LOCALE } from "@/lib/i18n/locale";

// Hrefs that appear in URL paths but aren't real pages — render as plain text.
const NON_LINKABLE = new Set<string>([]);

// Human-readable labels for path segments. Anything not listed falls back to
// title-cased segment text.
const LABELS_EN: Record<string, string> = {
  platform: "Platform",
  solutions: "Solutions",
  shop: "Shop",
  help: "Help",
  legal: "Legal",
  networking: "Sharing & Networking",
  "identity-and-sharing": "Identity & Sharing",
  "ai-suite": "AI Suite",
  "ai-tools": "AI Tools",
  "ai-enrichment": "AI Enrichment",
  ai: "AI Tools",
  crm: "CRM",
  "run-your-business": "Run your business",
  cards: "Digital Business Cards",
  nfc: "NFC Cards",
  sharing: "Sharing & Networking",
  contacts: "Contacts",
  pipelines: "Pipelines & Tasks",
  analytics: "Analytics",
  bookings: "Bookings",
  contracts: "Contracts",
  reviews: "Reviews",
  meetings: "Smart Meetings",
  events: "Events",
  tasks: "Tasks",
  scout: "AI Prospecting",
  automations: "Automations",
  integrations: "Integrations",
  "lead-exchange": "Lead Exchange",
  "welcome-email": "Welcome Email",
  wallet: "Apple & Google Wallet",
  "pin-protection": "PIN Protection",
  "qr-code": "QR Code",
  "email-signature": "Email Signature",
  sales: "For Sales Teams",
  founders: "For Founders",
  "real-estate": "For Real Estate",
  enterprise: "For Enterprise",
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  security: "Security",
  "legal-guidelines": "Legal Guidelines",
  "ticketing-global": "Ticketing Terms (Global)",
  "ticketing-vietnam": "Ticketing Terms (Vietnam)",
  "kyc-global": "KYC & Verification (Global)",
  "kyc-vietnam": "KYC & Verification (Vietnam)",
  "aml-global": "AML & Financial Crime (Global)",
  "aml-vietnam": "AML & Financial Crime (Vietnam)",
  "affiliate-global": "Affiliate Program (Global)",
  "affiliate-vietnam": "Affiliate Program (Vietnam)",
  "retailer-global": "Retailer Agreement (Global)",
  "retailer-vietnam": "Retailer Agreement (Vietnam)",
  returns: "Returns Policy",
};

const LABELS_VI: Record<string, string> = {
  platform: "Nền tảng",
  solutions: "Giải pháp",
  shop: "Cửa hàng",
  help: "Trợ giúp",
  legal: "Pháp lý",
  networking: "Chia sẻ & Kết nối",
  "identity-and-sharing": "Nhận diện & Chia sẻ",
  "ai-suite": "Bộ AI",
  "ai-tools": "Công cụ AI",
  "ai-enrichment": "Làm giàu dữ liệu AI",
  ai: "Công cụ AI",
  crm: "CRM",
  "run-your-business": "Vận hành doanh nghiệp",
  cards: "Danh thiếp số",
  nfc: "Thẻ NFC",
  sharing: "Chia sẻ & Kết nối",
  contacts: "Danh bạ",
  pipelines: "Pipeline & Task",
  analytics: "Phân tích",
  bookings: "Đặt lịch",
  contracts: "Hợp đồng",
  reviews: "Đánh giá",
  meetings: "Cuộc họp thông minh",
  events: "Sự kiện",
  tasks: "Công việc",
  scout: "Tìm kiếm AI",
  automations: "Tự động hoá",
  integrations: "Tích hợp",
  "lead-exchange": "Trao đổi lead",
  "welcome-email": "Email chào mừng",
  wallet: "Apple & Google Wallet",
  "pin-protection": "Bảo vệ bằng PIN",
  "qr-code": "Mã QR",
  "email-signature": "Chữ ký email",
  sales: "Cho đội bán hàng",
  founders: "Cho nhà sáng lập",
  "real-estate": "Cho bất động sản",
  enterprise: "Cho doanh nghiệp",
  privacy: "Chính sách bảo mật",
  terms: "Điều khoản dịch vụ",
  security: "Bảo mật",
  "legal-guidelines": "Hướng dẫn pháp lý",
  "ticketing-global": "Điều khoản vé (Toàn cầu)",
  "ticketing-vietnam": "Điều khoản vé (Việt Nam)",
  "kyc-global": "KYC & Xác minh (Toàn cầu)",
  "kyc-vietnam": "KYC & Xác minh (Việt Nam)",
  "aml-global": "AML & Tội phạm tài chính (Toàn cầu)",
  "aml-vietnam": "AML & Tội phạm tài chính (Việt Nam)",
  "affiliate-global": "Chương trình Affiliate (Toàn cầu)",
  "affiliate-vietnam": "Chương trình Affiliate (Việt Nam)",
  "retailer-global": "Hợp đồng Đại lý (Toàn cầu)",
  "retailer-vietnam": "Hợp đồng Đại lý (Việt Nam)",
  returns: "Chính sách Đổi trả",
};

const LABELS_EL: Record<string, string> = {
  platform: "Πλατφόρμα",
  solutions: "Λύσεις",
  shop: "Κατάστημα",
  help: "Βοήθεια",
  legal: "Νομικά",
  networking: "Κοινή χρήση & Δικτύωση",
  "identity-and-sharing": "Ταυτότητα & Κοινή χρήση",
  "ai-suite": "Σουίτα AI",
  "ai-tools": "Εργαλεία AI",
  "ai-enrichment": "Εμπλουτισμός AI",
  ai: "Εργαλεία AI",
  crm: "CRM",
  "run-your-business": "Διοικήστε την επιχείρησή σας",
  cards: "Ψηφιακές επαγγελματικές κάρτες",
  nfc: "Κάρτες NFC",
  sharing: "Κοινή χρήση & Δικτύωση",
  contacts: "Επαφές",
  pipelines: "Pipelines & Εργασίες",
  analytics: "Αναλυτικά",
  bookings: "Κρατήσεις",
  contracts: "Συμβόλαια",
  reviews: "Αξιολογήσεις",
  meetings: "Έξυπνες συναντήσεις",
  events: "Εκδηλώσεις",
  tasks: "Εργασίες",
  scout: "AI Prospecting",
  automations: "Αυτοματισμοί",
  integrations: "Ενσωματώσεις",
  "lead-exchange": "Ανταλλαγή leads",
  "welcome-email": "Email καλωσορίσματος",
  wallet: "Apple & Google Wallet",
  "pin-protection": "Προστασία PIN",
  "qr-code": "Κωδικός QR",
  "email-signature": "Υπογραφή email",
  sales: "Για ομάδες πωλήσεων",
  founders: "Για ιδρυτές",
  "real-estate": "Για ακίνητα",
  enterprise: "Για επιχειρήσεις",
  privacy: "Πολιτική Απορρήτου",
  terms: "Όροι Υπηρεσίας",
  security: "Ασφάλεια",
  "legal-guidelines": "Νομικές οδηγίες",
  "ticketing-global": "Όροι εισιτηρίων (Διεθνώς)",
  "ticketing-vietnam": "Όροι εισιτηρίων (Βιετνάμ)",
  "kyc-global": "KYC & Επαλήθευση (Διεθνώς)",
  "kyc-vietnam": "KYC & Επαλήθευση (Βιετνάμ)",
  "aml-global": "AML & Οικονομικό έγκλημα (Διεθνώς)",
  "aml-vietnam": "AML & Οικονομικό έγκλημα (Βιετνάμ)",
  "affiliate-global": "Πρόγραμμα Affiliate (Διεθνώς)",
  "affiliate-vietnam": "Πρόγραμμα Affiliate (Βιετνάμ)",
  "retailer-global": "Συμφωνία Μεταπωλητή (Διεθνώς)",
  "retailer-vietnam": "Συμφωνία Μεταπωλητή (Βιετνάμ)",
  returns: "Πολιτική Επιστροφών",
};

const LABELS_BY_LOCALE: Record<Locale, Record<string, string>> = {
  en: LABELS_EN,
  vi: LABELS_VI,
  el: LABELS_EL,
};

const HOME_LABEL: Record<Locale, string> = { en: "Home", vi: "Trang chủ", el: "Αρχική" };

function labelFor(seg: string, locale: Locale) {
  const map = LABELS_BY_LOCALE[locale] ?? LABELS_EN;
  if (map[seg]) return map[seg];
  if (locale !== "en" && LABELS_EN[seg]) return LABELS_EN[seg];
  return seg
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export type Crumb = { name: string; href: string };

export function buildCrumbsFromPath(pathname: string): Crumb[] {
  const all = pathname.split("/").filter(Boolean);
  let locale: Locale = DEFAULT_LOCALE;
  let segs = all;
  if (all.length > 0 && (LOCALES as string[]).includes(all[0])) {
    locale = all[0] as Locale;
    segs = all.slice(1);
  }
  const homeHref = locale === DEFAULT_LOCALE ? "/" : `/${locale}`;
  const crumbs: Crumb[] = [{ name: HOME_LABEL[locale], href: homeHref }];
  let acc = "";
  for (const s of segs) {
    acc += "/" + s;
    const href = locale === DEFAULT_LOCALE ? acc : `/${locale}${acc}`;
    crumbs.push({ name: labelFor(s, locale), href });
  }
  return crumbs;
}

export function SiteBreadcrumbs({
  trail,
  currentName,
  className = "",
}: {
  trail?: Crumb[];
  currentName?: string;
  className?: string;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const crumbs = (trail ?? buildCrumbsFromPath(pathname)).map((c, i, arr) =>
    currentName && i === arr.length - 1 ? { ...c, name: currentName } : c,
  );

  if (crumbs.length < 2) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.href.startsWith("http")
        ? c.href
        : `https://banana.cards${c.href === "/" ? "" : c.href}`,
    })),
  };

  return (
    <nav
      aria-label="Breadcrumb"
      className={
        "flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] font-medium text-muted-foreground sm:text-xs " +
        className
      }
    >
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        const linkable = !isLast && !NON_LINKABLE.has(c.href);
        return (
          <span key={c.href} className="inline-flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="h-3 w-3 opacity-50" />}
            {isLast ? (
              <span aria-current="page" className="font-semibold text-foreground">{c.name}</span>
            ) : linkable ? (
              <Link to={c.href as any} className="transition hover:text-foreground">
                {c.name}
              </Link>
            ) : (
              <span>{c.name}</span>
            )}
          </span>
        );
      })}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </nav>
  );
}